import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, NgModule, provideZoneChangeDetection } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatListModule } from '@angular/material/list';
import { MatRadioModule } from '@angular/material/radio';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';

import {
  VueAppDefaultsService,
  VueForgotCredentialsService,
  VueModule,
  VueTranslatePipe,
  VueTranslationService
} from '@vue/vueframework';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AccountMaintenanceComponent } from './account-maintenance/account-maintenance.component';
import { AffiliatesComponent } from './affiliates/affiliates.component';
import { AppDefaultsLocalService } from './app-defaults-local.service';
import { AppDefaultsService } from './app-defaults.service';
import { AppRootComponent } from './app-root/app-root.component';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ChangePasswordRecoveryComponent } from './change-password-recovery/change-password-recovery.component';
import { ChangePasswordComponent } from './change-password/change-password.component';
import { DownloadResourceFileComponent } from './download-resource-file/download-resource-file.component';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { LiteShellComponent } from './lite-shell/lite-shell.component';
import { PersonalInformationComponent } from './personal-information/personal-information.component';
import { SecurityQuestionsComponent } from './security-questions/security-questions.component';
import { PageMessagesComponent } from './shared/page-messages/page-messages.component';
import { AppConfigService } from './shared/services/app-config.service';
import { ForgotCredentialsService } from './shared/services/forgot-credentials.service';
import { SiteAffiliateNotAuthorizedComponent } from './site-affiliate-not-authorized/site-affiliate-not-authorized.component';
import { TedhubBackupComponent } from './tedhub-backup/tedhub-backup.component';
import { UsernameLookupComponent } from './username-lookup/username-lookup.component';
import { VueIdValidationComponent } from './vue-id-validation/vue-id-validation.component';

export function appConfigInitializerFn(
  appConfigService: AppConfigService,
  translationService: VueTranslationService
) {
  return async (): Promise<boolean> => {
    // Config needs to load first because it has the URLs for Xliff
    await appConfigService.loadAppConfig();

    // Bundles to preload
    const bundles = [
      'Connect',
      'TestCenterServices',
      'SiteMaintenanceSupportServices',
      'PortalBase',
      'CAS'
    ];
    await Promise.all(
      bundles.map((i) => firstValueFrom(translationService.getTranslationBundle(i)))
    );
    return true;
  };
}

@NgModule({ bootstrap: [ AppRootComponent ],
  declarations: [
    AppComponent,
    AccountMaintenanceComponent,
    ChangePasswordComponent,
    PersonalInformationComponent,
    SecurityQuestionsComponent,
    SiteAffiliateNotAuthorizedComponent,
    AffiliatesComponent,
    VueIdValidationComponent,
    LiteShellComponent,
    DownloadResourceFileComponent,
    AppRootComponent,
    PageMessagesComponent,
    TedhubBackupComponent,
    UsernameLookupComponent,
    ForgotPasswordComponent,
    ChangePasswordRecoveryComponent
  ], imports: [ AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    FontAwesomeModule,
    FormsModule,
    MatListModule,
    NgbModule,
    MatRadioModule,
    MatSnackBarModule,
    VueModule.forRoot() ], providers: [
    AppConfigService,
    {
      provide: VueAppDefaultsService,
      useClass: environment.production ?
        AppDefaultsService :
        AppDefaultsLocalService
    },
    {
      provide: VueForgotCredentialsService,
      useClass: ForgotCredentialsService
    },
    {
      deps: [ AppConfigService, VueTranslationService ],
      multi: true,
      provide: APP_INITIALIZER,
      useFactory: appConfigInitializerFn
    },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 2500 } },
    VueTranslatePipe,
    provideHttpClient(withInterceptorsFromDi()),
    provideZoneChangeDetection({ eventCoalescing: true })
  ] })
export class AppModule {}
